/**
 * Formats API monetary values, which are expressed in major currency units.
 * Intl supplies the correct currency precision (for example JPY/KRW/VND/CLP
 * have no fractional display digits) rather than inferring it from the amount.
 */
export const formatMoney = (amount: number, currency: string, locale?: string): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  } catch {
    return '-';
  }
};

const STRIPE_ZERO_DECIMAL_CURRENCIES = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf'
]);

const STRIPE_TWO_DECIMAL_ZERO_DISPLAY_CURRENCIES = new Set(['huf', 'idr']);

/** Formats a Stripe/market-config minor-unit amount returned by checkout. */
export const formatMinorMoney = (amount: number, currency: string, locale?: string): string => {
  // Stripe's charge precision is the API/storage contract. IDR and HUF are
  // stored in hundredths but this product displays them as whole major units;
  // pin the fraction digits because ICU metadata differs across Node releases.
  const normalizedCurrency = currency.toLowerCase();
  const divisor = STRIPE_ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency) ? 1 : 100;
  const majorAmount = amount / divisor;
  if (STRIPE_TWO_DECIMAL_ZERO_DISPLAY_CURRENCIES.has(normalizedCurrency)) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(majorAmount);
    } catch {
      return '-';
    }
  }
  return formatMoney(majorAmount, currency, locale);
};
